/**
 * Validators and grid-row rules shared by the Add Client and Add User screens. The regular expressions are copied
 * verbatim from the Angular components (add-client.component.ts / add-user.component.ts); the validators mirror
 * Angular's `Validators.required` / `maxLength` / `pattern` so the same values fail for the same reasons.
 */
import { NotificationTextMessage } from '@/core/enums/notification';
import { notifyError } from '@/core/notify/notify';

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const PHONE_PATTERN = /^[0-9+()\s-]{7,20}$/;
export const POSTCODE_PATTERN = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d[A-Za-z]{2}$/;

export interface FieldErrors {
  required?: true;
  maxlength?: { requiredLength: number; actualLength: number };
  pattern?: true;
  passwordMismatch?: true;
}

/** Angular `ValidatorFn` for a control value. */
export type Validator = (value: unknown) => FieldErrors | null;

const isEmpty = (value: unknown): boolean =>
  value === null || value === undefined || ((typeof value === 'string' || Array.isArray(value)) && value.length === 0);

/** `Validators.required` (whitespace is NOT empty, like Angular). */
export const required: Validator = (value) => (isEmpty(value) ? { required: true } : null);

/**
 * `required` that also rejects a value made only of spaces. Use it where the API trims the value (it rejects "   " as
 * empty), so the screen says so at once instead of after a round trip. `required` itself stays as Angular has it.
 */
export const requiredText: Validator = (value) =>
  isEmpty(value) || (typeof value === 'string' && value.trim().length === 0) ? { required: true } : null;

/** `Validators.maxLength(n)`. */
export const maxLength =
  (requiredLength: number): Validator =>
  (value) => {
    const length = (value as { length?: unknown } | null | undefined)?.length;
    return typeof length === 'number' && length > requiredLength
      ? { maxlength: { requiredLength, actualLength: length } }
      : null;
  };

/** `Validators.pattern(regex)` with a RegExp: empty values are valid, otherwise `regex.test(value)`. */
export const pattern =
  (regex: RegExp): Validator =>
  (value) => {
    if (isEmpty(value)) return null;
    return regex.test(value as string) ? null : { pattern: true };
  };

/** Runs validators in order and merges their errors (Angular `Validators.compose`). */
export function runValidators(value: unknown, validators: readonly Validator[]): FieldErrors | null {
  let merged: FieldErrors | null = null;
  for (const validator of validators) {
    const result = validator(value);
    if (result) merged = { ...(merged ?? {}), ...result };
  }
  return merged;
}

export interface AddressLine {
  addressType: string;
  addressLine: string;
  city: string;
  county: string;
  country: string;
  postCode: string;
}

/**
 * Address-row check of both screens: every non-empty row needs address, city/town and a valid post code. A row left
 * entirely blank (address, city, county, post code; the country is ignored) is skipped. First failure wins.
 * APT364: with `required` at least one address must be given (Add Client marks Address as mandatory).
 */
export function validateAddresses(rows: readonly AddressLine[], required = false): string | null {
  if (required && !rows.some((row) => row.addressLine?.trim() || row.city?.trim() || row.county?.trim() || row.postCode?.trim())) {
    return 'Address is required: enter the address, city/town and post code.';
  }
  for (const row of rows) {
    const hasAny = !!(row.addressLine || row.city || row.county || row.postCode);
    if (!hasAny) {
      continue;
    }
    if (!row.addressLine?.trim() || !row.city?.trim() || !row.postCode?.trim()) {
      return 'Each address needs an address, city/town and post code.';
    }
    if (!POSTCODE_PATTERN.test(row.postCode.trim())) {
      return `'${row.postCode}' is not a valid UK post code.`;
    }
  }
  return null;
}

/**
 * Angular `FormValidationService.validateForm(form)`: true when valid; otherwise the mandatory-fields toast (the
 * caller also marks every control touched so the red states show).
 */
export function validateForm(isValid: boolean): boolean {
  if (isValid) {
    return true;
  }
  notifyError(NotificationTextMessage.validationMessage);
  return false;
}
